/*
 * Copyright (c) 2022 NEXTWELL INTL SRL. All Rights Reserved.
 * This software is the proprietary information of NEXTWELL INTL SRL.
 * Use is subject to license and non-disclosure terms
 */

import { Component, EventEmitter, input, Input, model, output, Output } from '@angular/core';
import { LegalEntityAutocompleteService } from './legal-entity-autocomplete.service';
import { LegalEntityModel } from '@well/common-libs/legal-entity-management/domain-model';

@Component({
  selector: 'well-legal-entity-autocomplete',
  templateUrl: './legal-entity-autocomplete.component.html',
  styleUrls: ['./legal-entity-autocomplete.component.scss'],
  providers: [LegalEntityAutocompleteService],
})
export class LegalEntityAutocompleteComponent {
  results: LegalEntityModel[] = [];
  selected = model<LegalEntityModel | null>(null);
  country: string | null = null;

  @Input() placeholder: string | null = null;
  @Input() disabled = false;
  @Input() appendTo = 'body';
  @Input() searchLocation: 'ANAF' | 'ALL' = 'ALL';
  @Input() filterLocalCompaniesWithoutCompanyType = false;
  @Input() showAddButton = true;

  @Output() legalEntitySelect: EventEmitter<LegalEntityModel> = new EventEmitter();

  @Output() addCompany: EventEmitter<any> = new EventEmitter<any>();
  displaySelectedCompany = input<boolean>(false);
  clearSelectedCompany = output<void>();

  constructor(private legalEntityAutocompleteService: LegalEntityAutocompleteService) {}

  onQueryChange(query: string) {
    switch (this.searchLocation) {
      case 'ANAF':
        if (this.isValidRegistrationNumber(query)) {
          this.findAllFromAnaf({ query });
        } else {
          this.results = [];
        }
        break;

      case 'ALL':
        this.findAll(this.country, { query });
        break;
    }
  }

  onSelectItem(item: LegalEntityModel) {
    this.selected.set(item);
    this.legalEntitySelect.next(item);
  }

  onCountryChange(event: any) {
    this.country = event;
  }

  private findAllFromAnaf(event: any) {
    this.legalEntityAutocompleteService.findAllFromAnaf(event).subscribe((res) => {
      this.results = res;
    });
  }

  private findAllFromCoface(country: string, query: string) {
    this.legalEntityAutocompleteService.findAllFromCoface(country, query).subscribe((res) => {
      this.results = res;
    });
  }

  private findAll(country: any, event: any) {
    this.legalEntityAutocompleteService.findAll(country, event).subscribe((res) => {
      this.results = res;
      if (this.filterLocalCompaniesWithoutCompanyType) {
        this.results = this.results.filter((legalEntity: LegalEntityModel) => {
          return (legalEntity.origin === 'LOCAL' && legalEntity.companyType == null) || legalEntity.origin !== 'LOCAL';
        });
      }
    });
  }

  private isValidRegistrationNumber(regNumber: string) {
    return regNumber && regNumber.length >= 5;
  }

  addNewCompany() {
    this.addCompany.emit(true);
  }

  deleteSelected() {
    this.selected.set(null);
    this.clearSelectedCompany.emit();
  }

  getDisplayName = (item: LegalEntityModel | null): string => {
    return item?.companyName ?? '';
  };

  compareByUuid = (a: LegalEntityModel, b: LegalEntityModel): boolean => {
    return a?.uuid === b?.uuid;
  };
}
